import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import toast from 'react-hot-toast';
import {
  Save, RefreshCcw, DollarSign, Users,
  Calendar, Package, Layers, Zap, CheckSquare, Square,
  ShoppingCart, FileText, TrendingUp, TrendingDown,
} from 'lucide-react';
import { ALL_MODULES, PLAN_PRESETS } from '../../utils/organizationModules';

// Categorías parametrizables del modelo de límites por ciclo mensual.
// Ver sql-plan-limits-and-usage.sql y TrialContext.
const USAGE_CATEGORIES = [
  { key: 'sales',       label: 'Ventas',   icon: ShoppingCart, hint: 'Operaciones de venta (POS, profesional, web)' },
  { key: 'invoices',    label: 'Facturas', icon: FileText,     hint: 'Facturación electrónica emitida en el ciclo' },
  { key: 'purchases',   label: 'Compras',  icon: Package,      hint: 'Compras a proveedores' },
  { key: 'collections', label: 'Cobros',   icon: TrendingUp,   hint: 'Cobros a clientes (Tesorería)' },
  { key: 'payments',    label: 'Pagos',    icon: TrendingDown, hint: 'Pagos a proveedores (Tesorería)' },
];

const DEFAULT_LIMITS_BY_PLAN = {
  basic:      { sales: 500,  invoices: 500,  purchases: 200,  collections: 500,  payments: 200,  max_users: 3  },
  premium:    { sales: 2500, invoices: 2500, purchases: 1000, collections: 2500, payments: 1000, max_users: 6  },
  enterprise: { sales: 0,    invoices: 0,    purchases: 0,    collections: 0,    payments: 0,    max_users: 10 },
};

/**
 * SuperAdmin · Configuración global de Gastify
 * --------------------------------------------------------------
 * Permite al master admin (adpenia@gmail.com) controlar:
 *  · Días de prueba gratuita por defecto (default_trial_days)
 *  · Precio por sucursal / usuario extra (subscription_pricing)
 *  · Planes (basic / premium / enterprise) — precio mensual,
 *    límite de operaciones/mes, módulos habilitados.
 *
 * Persistencia:
 *  · subscription_pricing       → precios globales y por org
 *  · app_settings (id='global') → días de prueba + JSONB plans
 *  · ALL_MODULES y PLAN_PRESETS → fallback en cliente
 */

const DEFAULT_PLANS = {
  basic: {
    label: 'Básico',
    price_monthly: 50000,
    operation_limit: 500,
    modules: PLAN_PRESETS.basic,
    color: '#FDE68A',
    max_users: 3,
    monthly_limits: {
      sales: 500, invoices: 500, purchases: 200, collections: 500, payments: 200,
    },
  },
  premium: {
    label: 'Premium',
    price_monthly: 80000,
    operation_limit: 2500,
    modules: PLAN_PRESETS.premium,
    color: '#A7F3D0',
    max_users: 6,
    monthly_limits: {
      sales: 2500, invoices: 2500, purchases: 1000, collections: 2500, payments: 1000,
    },
  },
  enterprise: {
    label: 'Enterprise',
    price_monthly: 120000,
    operation_limit: 0, // 0 = ilimitado
    modules: PLAN_PRESETS.enterprise,
    color: '#C7D2FE',
    max_users: 10,
    monthly_limits: {
      sales: 0, invoices: 0, purchases: 0, collections: 0, payments: 0,
    },
  },
};

const PLAN_KEYS = ['basic', 'premium', 'enterprise'];
const BILLING_PROVIDER_OPTIONS = [
  {
    id: 'mercadopago',
    label: 'Mercado Pago',
    description: 'Queda como pasarela principal para las suscripciones nuevas.',
  },
  {
    id: 'astropay',
    label: 'AstroPay',
    description: 'Se mantiene disponible como alternativa legacy.',
  },
];

const groupModulesByCategory = () => {
  return ALL_MODULES.reduce((acc, m) => {
    const cat = m.category || 'Otros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});
};

export default function PricingConfig() {
  // El estado `global`/`orgOverrides`/`form` era para el modelo legacy
  // de cobro per-branch/per-user-extra. Ya no se usa en producción.
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePlanTab, setActivePlanTab] = useState('basic');

  const [trialDays, setTrialDays] = useState(14);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [subscriptionPaymentProvider, setSubscriptionPaymentProvider] = useState('mercadopago');

  const modulesByCategory = useMemo(groupModulesByCategory, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data: settings, error: settingsError } = await supabase
        .from('app_settings')
        .select('*')
        .eq('id', 'global')
        .maybeSingle();
      if (settingsError) throw settingsError;

      if (settings?.default_trial_days) {
        setTrialDays(Number(settings.default_trial_days) || 14);
      }
      if (settings?.subscription_payment_provider) {
        setSubscriptionPaymentProvider(
          settings.subscription_payment_provider === 'astropay'
            ? 'astropay'
            : 'mercadopago',
        );
      }
      // Cargar planes desde subscription_plans (fuente real para billing y TrialContext)
      const { data: subPlans, error: subPlansError } = await supabase
        .from('subscription_plans')
        .select('key, name, price_monthly, operation_limit, modules, max_users, monthly_limits, is_active, sort_order');

      let mergedFromSettings = { ...DEFAULT_PLANS };
      if (settings?.plans && typeof settings.plans === 'object') {
        for (const k of PLAN_KEYS) {
          if (settings.plans[k]) {
            mergedFromSettings[k] = { ...DEFAULT_PLANS[k], ...settings.plans[k] };
          }
        }
      }

      // Sobrescribir con lo que esté en subscription_plans (es lo que se cobra realmente)
      if (!subPlansError && Array.isArray(subPlans)) {
        for (const row of subPlans) {
          const k = row.key;
          if (!PLAN_KEYS.includes(k)) continue;
          mergedFromSettings[k] = {
            ...mergedFromSettings[k],
            label: row.name || mergedFromSettings[k].label,
            price_monthly: Number(row.price_monthly) || mergedFromSettings[k].price_monthly,
            operation_limit: Number(row.operation_limit) || 0,
            modules: Array.isArray(row.modules) ? row.modules : mergedFromSettings[k].modules,
            max_users:
              Number(row.max_users) ||
              DEFAULT_LIMITS_BY_PLAN[k]?.max_users ||
              mergedFromSettings[k].max_users,
            monthly_limits:
              (row.monthly_limits && typeof row.monthly_limits === 'object')
                ? row.monthly_limits
                : (mergedFromSettings[k].monthly_limits || {}),
          };
        }
      }

      setPlans(mergedFromSettings);
    } catch (e) {
      console.error('[PricingConfig] fetch error:', e);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveTrialAndPlans = async () => {
    setSaving(true);
    try {
      // 1) Guardar en app_settings (legacy: landing y registro lo leen)
      const payload = {
        id: 'global',
        default_trial_days: Number(trialDays) || 14,
        subscription_payment_provider:
          subscriptionPaymentProvider === 'astropay'
            ? 'astropay'
            : 'mercadopago',
        plans,
        updated_at: new Date().toISOString(),
      };
      const { error: settingsErr } = await supabase
        .from('app_settings')
        .upsert(payload, { onConflict: 'id' });
      if (settingsErr) throw settingsErr;

      // 2) Sincronizar a subscription_plans (fuente real de billing y TrialContext)
      // Upsert por key. Si la fila no existe, la crea con los defaults completos.
      const subPlansRows = PLAN_KEYS.map((k) => {
        const p = plans[k] || {};
        const safeLimits = {
          sales: Number(p.monthly_limits?.sales) || 0,
          invoices: Number(p.monthly_limits?.invoices) || 0,
          purchases: Number(p.monthly_limits?.purchases) || 0,
          collections: Number(p.monthly_limits?.collections) || 0,
          payments: Number(p.monthly_limits?.payments) || 0,
        };
        return {
          key: k,
          name: p.label || k,
          price_monthly: Number(p.price_monthly) || 0,
          currency: 'ARS',
          modules: Array.isArray(p.modules) ? p.modules : [],
          operation_limit: Number(p.operation_limit) || 0,
          max_users: Number(p.max_users) || DEFAULT_LIMITS_BY_PLAN[k]?.max_users || 1,
          monthly_limits: safeLimits,
          is_active: true,
          updated_at: new Date().toISOString(),
        };
      });

      const { error: subPlansErr } = await supabase
        .from('subscription_plans')
        .upsert(subPlansRows, { onConflict: 'key' });
      if (subPlansErr) {
        console.warn('[PricingConfig] subscription_plans upsert failed:', subPlansErr.message);
        toast.error(
          'Se guardó app_settings pero falló subscription_plans. Corré sql-plan-limits-and-usage.sql.',
          { duration: 7000 },
        );
      } else {
        toast.success('Configuración global guardada y planes sincronizados');
      }

      try {
        window.localStorage.setItem(
          'gastify:app-settings-updated',
          String(Date.now()),
        );
      } catch {}
    } catch (e) {
      toast.error(e?.message || 'Error al guardar planes');
    } finally {
      setSaving(false);
    }
  };

  const togglePlanModule = (planKey, moduleId) => {
    setPlans((prev) => {
      const current = new Set(prev[planKey].modules || []);
      if (current.has(moduleId)) current.delete(moduleId);
      else current.add(moduleId);
      return {
        ...prev,
        [planKey]: { ...prev[planKey], modules: Array.from(current) },
      };
    });
  };

  const togglePlanCategory = (planKey, categoryModules, allSelected) => {
    setPlans((prev) => {
      const current = new Set(prev[planKey].modules || []);
      categoryModules.forEach((m) => {
        if (allSelected) current.delete(m.id);
        else current.add(m.id);
      });
      return {
        ...prev,
        [planKey]: { ...prev[planKey], modules: Array.from(current) },
      };
    });
  };

  const updatePlanField = (planKey, field, value) => {
    setPlans((prev) => ({
      ...prev,
      [planKey]: { ...prev[planKey], [field]: value },
    }));
  };

  const updatePlanLimit = (planKey, kind, value) => {
    const numeric = Math.max(0, Number(value) || 0);
    setPlans((prev) => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        monthly_limits: {
          ...(prev[planKey]?.monthly_limits || {}),
          [kind]: numeric,
        },
      },
    }));
  };

  const resetPlanLimitsToDefaults = (planKey) => {
    const defaults = DEFAULT_LIMITS_BY_PLAN[planKey];
    if (!defaults) return;
    setPlans((prev) => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        max_users: defaults.max_users,
        monthly_limits: {
          sales: defaults.sales,
          invoices: defaults.invoices,
          purchases: defaults.purchases,
          collections: defaults.collections,
          payments: defaults.payments,
        },
      },
    }));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#E4E4E7] border-t-[#10B981] rounded-full animate-spin" />
    </div>
  );

  const activePlan = plans[activePlanTab];

  return (
    <div className="imp-page imp-container p-6 md:p-8">
      <div className="imp-header mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-[#3F3F46]">Configuración Gastify</h1>
          <p className="text-[13px] text-[#A1A1AA] mt-1">
            Días de prueba, precios y planes que ven todos los clientes nuevos
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-mint-100 text-mint-700 font-bold text-[12px] hover:bg-mint-500 hover:text-white transition-all"
        >
          <RefreshCcw size={14} /> Recargar
        </button>
      </div>

      {/* ── BLOQUE 1: Días de prueba ── */}
      <section className="bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-6 shadow-sm mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-md bg-mint-100 flex items-center justify-center">
            <Calendar size={18} className="text-mint-600" />
          </div>
          <div>
            <h2 className="text-[14px] font-black text-[#3F3F46]">Período de prueba</h2>
            <p className="text-[12px] text-[#A1A1AA]">Días gratis al registrarse — aplica a todos los nuevos clientes</p>
          </div>
        </div>

        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1">
            <label className="block text-[11px] font-bold text-[#3F3F46] mb-1.5 uppercase tracking-wider">
              Días de prueba
            </label>
            <input
              type="number" min="0" max="365"
              value={trialDays}
              onChange={(e) => setTrialDays(Number(e.target.value))}
              className="w-full px-4 py-2.5 rounded-md border border-[#E4E4E7] text-[14px] font-bold text-[#3F3F46] outline-none focus:border-mint-500 focus:ring-2 focus:ring-mint-500/10 transition-all"
            />
          </div>
          <div className="text-[12px] text-[#A1A1AA] pb-3">ej: 14 = 2 semanas gratis</div>
        </div>
        <div className="mt-6 pt-6 border-t border-[#E4E4E7]">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-mint-100 flex items-center justify-center">
              <Save size={18} className="text-mint-600" />
            </div>
            <div>
              <h2 className="text-[14px] font-black text-[#3F3F46]">Proveedor de suscripciones</h2>
              <p className="text-[12px] text-[#A1A1AA]">
                Elige la pasarela global que usara Gastify para todos los abonos mensuales
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {BILLING_PROVIDER_OPTIONS.map((option) => {
              const selected = subscriptionPaymentProvider === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSubscriptionPaymentProvider(option.id)}
                  className={`text-left rounded-md border p-4 transition-all ${
                    selected
                      ? 'border-mint-500 bg-mint-50 shadow-sm'
                      : 'border-[#E4E4E7] hover:border-mint-300 bg-[var(--surface)]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[12px] font-black uppercase tracking-widest text-[#3F3F46]">
                        {option.label}
                      </p>
                      <p className="text-[12px] text-[#71717A] mt-1">{option.description}</p>
                    </div>
                    <div
                      className={`mt-0.5 h-5 w-5 rounded-full border flex items-center justify-center ${
                        selected
                          ? 'border-mint-500 bg-mint-500 text-white'
                          : 'border-[#D4D4D8] text-transparent'
                      }`}
                    >
                      <CheckSquare size={12} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-3 rounded-md bg-cream-50 border border-[#E4E4E7] px-4 py-3">
            <p className="text-[11px] font-bold text-[#52525B]">
              {subscriptionPaymentProvider === 'mercadopago'
                ? 'Mercado Pago requiere al menos MERCADOPAGO_ACCESS_TOKEN en Supabase. Para validar webhooks, agrega tambien MERCADOPAGO_WEBHOOK_SECRET.'
                : 'AstroPay requiere ASTROPAY_GATEWAY_API_KEY, ASTROPAY_GATEWAY_SECRET_KEY y ASTROPAY_API_URL en Supabase.'}
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOQUE 2: Planes ── */}
      <section className="bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-6 shadow-sm mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-md bg-mint-100 flex items-center justify-center">
            <Layers size={18} className="text-mint-600" />
          </div>
          <div>
            <h2 className="text-[14px] font-black text-[#3F3F46]">Planes y límites</h2>
            <p className="text-[12px] text-[#A1A1AA]">Precio mensual, operaciones permitidas y módulos por plan</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E4E4E7] mb-6 overflow-x-auto no-scrollbar pb-1">
          {PLAN_KEYS.map((key) => {
            const p = plans[key];
            const active = activePlanTab === key;
            return (
              <button
                key={key}
                onClick={() => setActivePlanTab(key)}
                className={`px-5 py-3 text-[12px] font-black uppercase tracking-wider transition-all border-b-2 whitespace-nowrap shrink-0 ${
                  active ? 'border-mint-500 text-mint-700' : 'border-transparent text-[#A1A1AA] hover:text-[#3F3F46]'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Plan editor — precio + usuarios + total módulos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-[11px] font-bold text-[#3F3F46] mb-1.5 uppercase tracking-wider flex items-center gap-1.5">
              <DollarSign size={12} className="text-mint-600" /> Precio mensual
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] text-[13px] font-bold">$</span>
              <input
                type="number" min="0"
                value={activePlan.price_monthly}
                onChange={(e) => updatePlanField(activePlanTab, 'price_monthly', Number(e.target.value))}
                className="w-full pl-7 pr-4 py-2.5 rounded-md border border-[#E4E4E7] text-[13px] font-bold text-[#3F3F46] outline-none focus:border-mint-500 focus:ring-2 focus:ring-mint-500/10 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-[#3F3F46] mb-1.5 uppercase tracking-wider flex items-center gap-1.5">
              <Users size={12} className="text-mint-600" /> Usuarios incluidos
            </label>
            <input
              type="number" min="1"
              value={activePlan.max_users || 1}
              onChange={(e) => updatePlanField(activePlanTab, 'max_users', Math.max(1, Number(e.target.value) || 1))}
              className="w-full px-4 py-2.5 rounded-md border border-[#E4E4E7] text-[13px] font-bold text-[#3F3F46] outline-none focus:border-mint-500 focus:ring-2 focus:ring-mint-500/10 transition-all"
            />
            <p className="text-[10px] text-[#A1A1AA] mt-1">Tope de cuentas activas</p>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-[#3F3F46] mb-1.5 uppercase tracking-wider flex items-center gap-1.5">
              <Package size={12} className="text-mint-600" /> Módulos habilitados
            </label>
            <div className="px-4 py-2.5 rounded-md border border-[#E4E4E7] bg-cream-50 text-[13px] font-bold text-[#3F3F46]">
              {activePlan.modules.length} / {ALL_MODULES.length}
            </div>
          </div>
        </div>

        {/* Límites mensuales por categoría — parametrizables */}
        <div className="border border-[#E4E4E7] rounded-md p-4 bg-cream-50 mb-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-[11px] font-black uppercase tracking-widest text-[#A1A1AA] flex items-center gap-1.5">
                <Zap size={12} className="text-mint-600" /> Operaciones permitidas / ciclo mensual
              </p>
              <p className="text-[11px] text-[#71717A] mt-1">
                Se reinician cada mes el mismo día del vencimiento. Poné <strong>0</strong> para ilimitado.
              </p>
            </div>
            <button
              type="button"
              onClick={() => resetPlanLimitsToDefaults(activePlanTab)}
              className="text-[10px] font-black uppercase tracking-[0.14em] text-mint-700 hover:underline whitespace-nowrap"
            >
              Restaurar defaults
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {USAGE_CATEGORIES.map(({ key, label, icon: Icon, hint }) => {
              const value = Number(activePlan?.monthly_limits?.[key]) || 0;
              const isUnlimited = value === 0;
              return (
                <div key={key} className="rounded-md border border-[#E4E4E7] bg-[var(--surface)] p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#3F3F46] flex items-center gap-1.5">
                      <Icon size={11} className="text-mint-600" />
                      {label}
                    </span>
                    {isUnlimited && (
                      <span className="text-[9px] font-black uppercase tracking-wider text-mint-700">∞</span>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={value}
                    onChange={(e) => updatePlanLimit(activePlanTab, key, e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-[#E4E4E7] text-[13px] font-bold text-[#3F3F46] outline-none focus:border-mint-500 focus:ring-2 focus:ring-mint-500/10 transition-all"
                  />
                  <p className="text-[10px] text-[#A1A1AA] mt-1 leading-tight">{hint}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Modules selector */}
        <div className="border border-[#E4E4E7] rounded-md p-4 bg-cream-50">
          <p className="text-[11px] font-black uppercase tracking-widest text-[#A1A1AA] mb-3">
            Módulos del plan {activePlan.label}
          </p>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
            {Object.entries(modulesByCategory).map(([category, mods]) => {
              const allSelected = mods.every((m) => activePlan.modules.includes(m.id));
              const someSelected = mods.some((m) => activePlan.modules.includes(m.id));
              return (
                <div key={category}>
                  <button
                    onClick={() => togglePlanCategory(activePlanTab, mods, allSelected)}
                    className="flex items-center gap-2 mb-2 text-[11px] font-black uppercase tracking-widest text-[#3F3F46] hover:text-mint-700 transition-colors"
                  >
                    {allSelected ? (
                      <CheckSquare size={14} className="text-mint-600" />
                    ) : someSelected ? (
                      <CheckSquare size={14} className="text-mint-500 opacity-50" />
                    ) : (
                      <Square size={14} className="text-[#A1A1AA]" />
                    )}
                    {category}
                    <span className="text-[10px] font-bold text-[#A1A1AA]">
                      ({mods.filter((m) => activePlan.modules.includes(m.id)).length}/{mods.length})
                    </span>
                  </button>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 ml-5">
                    {mods.map((m) => {
                      const checked = activePlan.modules.includes(m.id);
                      return (
                        <label
                          key={m.id}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-[12px] font-medium transition-all ${
                            checked
                              ? 'bg-mint-100 text-mint-700 border border-mint-300'
                              : 'bg-[var(--surface)] text-[#3F3F46] border border-[#E4E4E7] hover:border-mint-500'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePlanModule(activePlanTab, m.id)}
                            className="hidden"
                          />
                          {checked ? <CheckSquare size={12} /> : <Square size={12} className="text-[#A1A1AA]" />}
                          <span className="truncate">{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Save button */}
      <div className="mb-8">
        <button
          onClick={saveTrialAndPlans}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-md bg-mint-500 text-white font-black text-[12px] uppercase tracking-widest hover:bg-mint-600 transition-all disabled:opacity-50 shadow-lg shadow-mint-500/20"
        >
          {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
          Guardar prueba, proveedor y planes
        </button>
      </div>

      {/* El modelo de cobro por sucursal/usuario extra fue retirado.
          El billing real son los planes fijos del bloque de arriba +
          la tabla subscription_plans en DB. Si hay overrides per-org
          legados en subscription_pricing, se pueden consultar por SQL. */}
    </div>
  );
}
